import {
  Bin,
  BreadcrumbHeading,
  Chip,
  EditIcon,
  EllipsisMenu,
  Plus,
  PrimaryButton,
  RetryIcon,
  SearchField,
  Spinner,
  Table,
  useNotifications,
  ViewIcon,
} from '@weareopenr/component-library';
import styles from './Users.module.scss';
import { useMutation, useInfiniteQuery } from '@tanstack/react-query';
import { getUsers, GetUsersResponse } from 'src/api/getUsers/getUsers';
import { IUser, IRole } from 'src/types';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { IOption } from '@weareopenr/component-library/build/components/Dropdown/Dropdown';
import { useMemo, useState } from 'react';
import UserModal from 'src/components/UserModal/UserModal';
import { reactivateUser as reactivateUserApi } from 'src/api/reactivateUser/reactivateUser';
import { useInView } from 'react-intersection-observer';
import { useRoles } from 'src/hooks/useRoles/useRoles';
import { useHistory } from 'react-router-dom';
import { usePermissions, useUserTracking } from 'src/hooks';
import { useGetBrands } from 'src/hooks/useGetBrands/useGetBrands';
import { IBrandResponse } from 'src/api/getBrands/types';
import { BrandsAvatarGroup } from 'src/components/BrandsAvatarGroup';
import { useDeactivateUser } from 'src/components/DeactivateUser';

export const Users = () => {
  const history = useHistory();
  const createNotification = useNotifications();
  const { trackEvent, captureException } = useUserTracking();
  const [showInviteModal, setShowInviteModal] = useState<boolean>(false);
  const [editUser, setEditUser] = useState<IUser>();
  const { canCreateUsers, canEditUsers, canDeactivateUsers } = usePermissions();
  const { roles } = useRoles();
  const { data: brands } = useGetBrands();

  const roleNameMap = useMemo(() => {
    return roles.reduce((map: Record<string, string>, role: IRole) => {
      map[role.slug] = role.name;
      return map;
    }, {});
  }, [roles]);

  const { data, isLoading, refetch, fetchNextPage } = useInfiniteQuery(
    ['users'],
    ({ pageParam }) => getUsers(pageParam),
    {
      getNextPageParam: (lastPage: GetUsersResponse) => lastPage.paging?.after ?? undefined,
    },
  );
  const { ref } = useInView({
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
      }
    },
  });

  const users = data?.pages.flatMap((page: GetUsersResponse) => page.data);
  const [searchFilter, setSearchFilter] = useState<string>('');

  const filteredUsers = useMemo(() => {
    if (!searchFilter) {
      return users;
    }
    return users?.filter(
      (user) =>
        user.email.toLowerCase().includes(searchFilter.toLowerCase()) ||
        user.last_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
        user.first_name.toLowerCase().includes(searchFilter.toLowerCase()),
    );
  }, [users, searchFilter]);

  const deactivateUserHook = useDeactivateUser();

  const { mutate: reactivateUser } = useMutation(reactivateUserApi, {
    onSuccess: (_, { email }) => {
      trackEvent('user_reactivated', { email });
      createNotification({ severity: 'success', message: 'Reactivated ' + email });
      refetch();
    },
    onError: (error, { email }) => {
      createNotification({
        severity: 'error',
        message: `Unable to reactivate user (${email}), please try again`,
      });
      captureException(error);
    },
  });

  if (isLoading) {
    return (
      <div className={styles.spinner}>
        <Spinner size="xlarge" colour="rainbow" />
      </div>
    );
  }

  const columnHelper = createColumnHelper<IUser>();

  const tableRowActions = (info: CellContext<IUser, unknown>) => {
    const user = info.row.original;
    const ellipsisOptions: IOption[] = [
      {
        id: 'view',
        name: 'View',
        onOptionClick: () => {
          history.push(`/users/${user.id}`);
        },
        icon: ViewIcon,
      },
    ];
    if (canEditUsers)
      ellipsisOptions.push({
        id: 'edit',
        name: 'Edit',
        onOptionClick: () => {
          setEditUser(user);
          setShowInviteModal(true);
        },
        icon: EditIcon,
      });
    if (canDeactivateUsers && user.state === 'active') {
      ellipsisOptions.push(
        { id: 'd-1', isDivider: true },
        {
          id: 'deactivate',
          name: 'Deactivate',
          onOptionClick: () => deactivateUserHook.openPrompt(user),
          icon: Bin,
          hoverColour: 'tomato',
        },
      );
    }
    if (canEditUsers && user.state === 'inactive') {
      ellipsisOptions.push(
        { id: 'd-1', isDivider: true },
        {
          id: 'reactivate',
          name: 'Reactivate',
          onOptionClick: () => reactivateUser(user),
          icon: RetryIcon,
          hoverColour: 'kale',
        },
      );
    }
    return (
      <div className={styles['ellipsis-menu-container']}>
        {ellipsisOptions.length > 0 && (
          <EllipsisMenu options={ellipsisOptions} variant="ghost" hoverStyle="high-contrast" />
        )}
      </div>
    );
  };

  const renderUserState = (info: CellContext<IUser, string>) => (
    <Chip
      key={info.row.original.id}
      label={info.row.original.state.charAt(0).toUpperCase() + info.row.original.state.slice(1)}
      backgroundColour={info.row.original.state === 'active' ? 'kale' : 'liquorice'}
    />
  );

  const renderBrands = (info: CellContext<IUser, string[]>) => {
    const userBrands: IBrandResponse[] =
      info.row.original.brands?.[0] === '*'
        ? brands || []
        : info.row.original.brands
            ?.map((brand) => brands?.find((b) => b.brand_id === brand))
            .filter((brand): brand is IBrandResponse => brand !== undefined) || [];

    return userBrands ? <BrandsAvatarGroup brands={userBrands} /> : 'No Brands';
  };

  const columns = [
    columnHelper.display({
      id: 'name',
      header: 'Name',
      cell: (info) => `${info.row.original.first_name} ${info.row.original.last_name}`,
    }),
    columnHelper.accessor('email', {
      header: 'Email',
    }),
    columnHelper.accessor('role', {
      header: 'Role',
      size: 180,
      cell: (info) => {
        const roleSlug = info.row.original.role;
        return roleSlug
          ? roleNameMap[roleSlug] || roleSlug.charAt(0).toUpperCase() + roleSlug.slice(1)
          : '';
      },
    }),
    columnHelper.accessor('brands', {
      header: 'Brands',
      size: 150,
      cell: renderBrands,
    }),
    columnHelper.accessor('state', {
      header: 'Status',
      size: 100,
      cell: renderUserState,
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 60,
      cell: tableRowActions,
    }),
  ];

  return (
    <>
      {(canCreateUsers || canEditUsers) && (
        <UserModal
          show={showInviteModal}
          closeModal={() => {
            refetch();
            setShowInviteModal(false);
            setEditUser(undefined);
          }}
          user={editUser}
        />
      )}
      {deactivateUserHook.PromptComponent}
      <div className={styles.container}>
        <BreadcrumbHeading
          breadcrumbs={[{ label: 'Users', path: '/users' }]}
          primaryButton={
            canCreateUsers ? (
              <PrimaryButton
                onClick={() => setShowInviteModal(true)}
                icon={Plus}
                aria-label="Create User"
              >
                Create User
              </PrimaryButton>
            ) : undefined
          }
        />
        <div className={styles['filter-container']}>
          <SearchField
            placeholder="Filter Users"
            onChange={(e) => setSearchFilter(e.target.value)}
            isDisabled={false}
          />
        </div>
        <Table columns={columns} data={filteredUsers || []} inViewTrackingRef={ref} />
      </div>
    </>
  );
};
